import * as Yup from 'yup';
import { maxValidationNumber, maxValidationString, minValidationNumber, minValidationString, requiredField, validateFileSize, validateSingleFileSize } from '../../helperFuncs';
import { matchIsValidTel } from 'mui-tel-input';
import UserService from '../../../services/UserService';
import { IntExt } from '../../permissions';
import i18n from '../../../i18n';

export const CreateProfessionalValidation = Yup.object({
  // step 1
  title:
    Yup.string()
      .required(requiredField),
  first_name:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3))
      .max(40, maxValidationString(40)),
  last_name:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3))
      .max(40, maxValidationString(40)),
  phone_number:
    Yup.string()
      .required(requiredField)
      .test('phone', 'Παρακαλώ προσθέστε έγκυρη μορφή τηλεφώνου', (value) => matchIsValidTel(value || '')),
  email:
    Yup.string()
      .required(requiredField)
      .email('Μη αποδεκτή μορφή Email')
      .matches(/^\w+([.-]?\w+)*@\w+([-]?\w+)*(\.\w{2,}(?!\.\w))$/, 'Μη αποδεκτή μορφή Email'),
  afm: Yup.string()
    .required(requiredField)
    .min(9, minValidationString(9))
    .matches(/^[0-9]+$/, 'Παρακαλούμε συμπληρώστε μόνο αριθμούς')
    .max(9, maxValidationString(9)),
  // step 2
  ...(i18n.language !== 'en' &&
  {
    services:
      Yup.string()
        .required(requiredField)
        .min(3, minValidationString(3))
  }),
  ...(UserService.hasRole([IntExt.External]) &&
    i18n.language === 'en' && {
    services_en:
      Yup.string()
        .required(requiredField)
        .min(3, minValidationString(3))
  }),
  ...(i18n.language !== 'en' &&
  {
    productions:
      Yup.string()
        .required(requiredField)
        .min(3, minValidationString(3))
  }),
  ...(UserService.hasRole([IntExt.External]) &&
    i18n.language === 'en' && {
    productions_en:
      Yup.string()
        .required(requiredField)
        .min(3, minValidationString(3))
  }),
  languages:
    Yup.array()
      .required(requiredField),
  regions:
    Yup.array()
      .required(requiredField),
  // step3
  city:
    Yup.string()
      .nullable()
      .required(requiredField),
  tk:
    Yup.number()
      .nullable()
      .required(requiredField)
      .min(10000, minValidationNumber(10000))
      .max(99999, maxValidationNumber(99999)),
  address:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3)),
  address_number:
    Yup.string()
      .nullable()
      .min(1, minValidationNumber(1))
      .max(5, maxValidationNumber(5))
      // eslint-disable-next-line no-useless-escape
      .matches(/^\d+([A-Za-z]|\-\d+)?$/, 'Μη αποδεκτή μορφή Αριθμού Διεύθυνσης'),
  // step4
  thumbnail:
    Yup.mixed()
      .nullable()
      .test('thumbnail', 'Παρακαλώ ανεβάστε αρχείο μέχρι 5MB', (value) => validateSingleFileSize(value, 5)),
  media:
    Yup.mixed()
      .nullable()
      .test('media', 'Παρακαλώ ανεβάστε αρχείο μέχρι 15MB', (value) => validateFileSize(value, 15))
      .test('mediaLength', 'Παρακαλώ ανεβάστε μέχρι 10 αρχεία', (value) => {
        if (!value) return true;
        return value?.length <= 10;
      }),
  // step5
  resume:
    Yup.mixed()
      .nullable()
      .test('resume', 'Παρακαλώ ανεβάστε αρχείο μέχρι 4MB', (value) => validateSingleFileSize(value))
  ,
  attached_files:
    Yup.mixed()
      .nullable()
      .test('attached_files', 'Παρακαλώ ανεβάστε αρχείο μέχρι 4MB', (value) => validateFileSize(value))
      .test('attached_files_length', 'Παρακαλώ ανεβάστε μέχρι 10 αρχεία', (value) => {
        if (!value) return true;
        return value?.length <= 10;
      })
  ,
}).shape({
  category_level_1:
    Yup.array()
      .when('specialty_lvl2', {
        is: (specialty_lvl2: any) => (!specialty_lvl2 || specialty_lvl2.length === 0),
        then:
          Yup.array()
            .required('Πρέπει να συμπληρώσετε τουλάχιστον ένα από τα 2 πεδία')
            .min(1, 'Πρέπει να συμπληρώσετε τουλάχιστον ένα πεδίο')
      }),
  specialty_lvl2:
    Yup.array()
      .when('category_level_1', {
        is: (category_level_1: any) => (!category_level_1 || category_level_1.length === 0),
        then:
          Yup.array()
            .required('Πρέπει να συμπληρώσετε τουλάχιστον ένα από τα 2 πεδία')
            .min(1, 'Πρέπει να συμπληρώσετε τουλάχιστον ένα πεδίο')
      }),
}, [['category_level_1', 'specialty_lvl2']]);

export const validationSchemaCommForm = Yup.object({
  first_name:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3))
      .max(40, maxValidationString(40)),
  last_name:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3))
      .max(40, maxValidationString(40)),
  phone:
    Yup.string()
      .required(requiredField)
      .test('phone', 'Μη αποδεκτή μορφή Τηλεφώνου', (value) => matchIsValidTel(value || '')),
  email:
    Yup.string()
      .required(requiredField)
      .email('Μη αποδεκτή μορφή Email')
      .matches(/^\w+([.-]?\w+)*@\w+([-]?\w+)*(\.\w{2,}(?!\.\w))$/, 'Μη αποδεκτή μορφή Email'),
  descr_co:
    Yup.string()
      .required(requiredField)
      .min(3, minValidationString(3))
      .max(600, maxValidationString(600)),
  termsAndConditions:
    Yup.boolean()
      .required(requiredField)
      .test('checkboxTest', 'Αποδοχή Όρων Χρήσης & Ορθότητας Στοιχείων', (value) => value === true),
});

export const validationSchema = Yup.object({
  free_text:
    Yup.string().max(60, maxValidationString(60)),
});